import { FC, useEffect, useRef } from 'react';
import { useFeature } from '@growthbook/growthbook-react';

import {
  INVITE_STEP_OPTIMIZATION_FEATURE_KEY,
  mergedInviteOptimizationVariant,
  normalizeInviteOptimizationVariantId,
  shouldPersistInviteOptimizationVariant,
} from '@/components/Steps/InviteTeamStep/inviteStepOptimization.util';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { selectCompanyId } from '@/redux/features/authSlice';
import { setInviteOptimizationResolved } from '@/redux/features/onboardingSlice';
import CompanyRequests from '@/requests/company.requests';
import { ExperimentProps } from '@/types';
import SentryService from '@/utils/sentry-service.util';

function handleInviteVariantPersistError(error: unknown): void {
  SentryService.captureExceptionWithRetryAfter(error, {
    errorContext: 'InviteOptimizationReduxSync.putCompanySettings',
  }).catch(() => {
    /* ignore failures while reporting the original error */
  });
}

/**
 * Keeps Redux `inviteOptimizationResolved` aligned with persisted company custom (sticky resume)
 * and GrowthBook, so `defaultFlowModel.getSteps()` can insert variant C's extra step.
 * Persists merged variant to `custom.inviteStepOptimizationVariant` when it differs from API.
 */
export const InviteOptimizationReduxSync: FC = () => {
  const dispatch = useAppDispatch();
  const companyId = useAppSelector(selectCompanyId);
  const company = useAppSelector(state => state.auth.company);
  const inviteStepOptimization = useFeature<ExperimentProps>(INVITE_STEP_OPTIMIZATION_FEATURE_KEY);
  const customVariant = company?.companySettings?.custom?.inviteStepOptimizationVariant;
  const merged = mergedInviteOptimizationVariant(customVariant, inviteStepOptimization.value);
  const lastSessionPutRef = useRef<typeof merged>(undefined);

  useEffect(() => {
    dispatch(setInviteOptimizationResolved(merged));
  }, [dispatch, merged]);

  useEffect(() => {
    if (!companyId) {
      return;
    }

    const onServer = normalizeInviteOptimizationVariantId(customVariant);

    if (merged === onServer) {
      lastSessionPutRef.current = merged;
      return;
    }

    if (
      merged === undefined ||
      !shouldPersistInviteOptimizationVariant(
        companyId,
        merged,
        onServer,
        lastSessionPutRef.current,
      )
    ) {
      return;
    }

    lastSessionPutRef.current = merged;
    const existing = company?.companySettings?.custom ?? {};
    CompanyRequests.putCompanySettings(companyId, {
      custom: {
        ...existing,
        inviteStepOptimizationVariant: merged,
      },
    }).catch(handleInviteVariantPersistError);
  }, [companyId, merged, customVariant, company?.companySettings?.custom]);

  useEffect(() => {
    lastSessionPutRef.current = undefined;
  }, [companyId]);

  return null;
};
